import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import NewActivityParticipant from "@/components/activities/NewActivityParticipant.vue";
import DataTable from "@/components/layout/DataTable.vue";
import Card from "@/components/layout/Card.vue";
import {computed, ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import {Participant} from "@/types/entities/activity";
import {Item} from "vue3-easy-data-table";
import {useParticipantService} from "@/inversify.config";
import {SucceededOrNotResponse} from "@/types/responses";
import {notifyError, notifySuccess} from "@/notify";

interface propsInterface {
  activityId: string
  activityOccurrenceId: string,

  participants: Participant[],
  maxParticipants?: number,
  participantsAreLoading: boolean,
  participantsSearchValue: string
  isOnlineActivity: boolean,
}

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityParticipantTable',
  props: {
    activityId: {},
    activityOccurrenceId: {},
    participants: {},
    maxParticipants: {},
    participantsAreLoading: { type: Boolean },
    participantsSearchValue: {},
    isOnlineActivity: { type: Boolean }
  },
  emits: ["newParticipantAdded"],
  setup(__props: any, { emit: __emit }) {

const {t} = useI18n();

// eslint-disable-next-line
const props = __props;

const emit = __emit;

const participants = ref(props.participants);
const activityOccurrenceId = ref(props.activityOccurrenceId);
const activityId = ref(props.activityId);
const participantsAreLoading = ref(props.participantsAreLoading);
const participantsSearchValue = ref(props.participantsSearchValue)
const isOnline = ref(props.isOnlineActivity);
const maxParticipants = ref(props.maxParticipants ?? 0);
const participantService = useParticipantService();

watch(props, (newProps) => {
  activityId.value = newProps.activityId;
  activityOccurrenceId.value = newProps.activityOccurrenceId;
  participants.value = newProps.participants;
  participantsAreLoading.value = newProps.participantsAreLoading;
  participantsSearchValue.value = newProps.participantsSearchValue;
  isOnline.value = newProps.isOnlineActivity;
  maxParticipants.value = newProps.maxParticipants!;
});

const participantsItems = computed(() => {
  let items: Item[] = [];
  if (participants.value == null || participants.value.length == 0)
    return [];

  participants.value.forEach((participant: Participant) => {
    const actions: any = {
      edit: participant.activityRegistrationId !== null,
      delete: participant.activityRegistrationId === null
    };

    if (participant.activityRegistrationId) {
      actions.transferRegistration = {
        name: "admin.children.activities.participantTransfer",
        params: {
          participantId: participant.id,
          activityOccurrenceId: activityOccurrenceId.value,
        }
      };
    } else {
      actions.transferParticipant = {
        name: "admin.children.activities.participantTransfer",
        params: {
          participantId: participant.id,
          activityOccurrenceId: activityOccurrenceId.value,
        }
      };
    }
    items.push({
      ...participant,
      memberNumber: participant.memberNumber?.length ? participant.memberNumber : t("global.undefined"),
      organizationName: participant.organizationName?.length ? participant.organizationName : t("global.undefined"),
      reducedMobility: participant.reducedMobility ? t('global.yes') : t('global.no'),
      actions: actions
    });
  });
  return items
})

const participantsHeaders = computed(() => {
  let headers =
    [
      {
        text: t("participants.memberNumber"),
        value: "memberNumber",
        sortable: true,
        width: 250
      },
      {
        text: t("participants.organization"),
        value: "organizationName",
        sortable: true
      },
      {
        text: t("participants.lastName"),
        value: "lastName",
        sortable: true
      },
      {
        text: t("participants.firstName"),
        value: "firstName",
        sortable: true
      },
      {
        text: t("participants.email"),
        value: "emailAddress",
        sortable: true
      }
    ];

  if (!isOnline.value) {
    headers.push(
      {
        text: t("participants.allergies"),
        value: "allergies",
        sortable: true
      },
      {
        text: t("participants.reducedMobility"),
        value: "reducedMobility",
        sortable: true
      },
    )
  }

  headers.push(
    {
      text: t("global.table.actions"),
      value: "actions",
      sortable: false
    })
  return headers
})

function handleEdit(id: string) {
  let participant = participants.value.find(x => x.id == id);
  if (!participant)
    return;
  const appUrl = new URL(window.location.href);
  let url = new URL(`${appUrl.origin}/activites-et-formations/inscription`);
  const params = new URLSearchParams({
    activityOccurrenceId: activityOccurrenceId.value,
    activityRegistrationId: participant.activityRegistrationId!
  });
  url.search = params.toString();
  window.location.href = url.toString();
}

async function handleDelete(participant: Participant) {
  if (!participant || !participant.id || !participants.value.some(x => x.id == participant.id))
    return;

  let confirmDelete = confirm(t("participants.form.deleteConfirmation"));
  if (!confirmDelete)
    return;

  let result: SucceededOrNotResponse = await participantService.deleteParticipant(participant.id);
  if (result?.succeeded) {
    notifySuccess(t('participants.form.deleteSuccess'))
    participants.value = participants.value.filter(x => x.id !== participant.id);
  } else {
    notifyError(t('participants.form.deleteError'))
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Card, {
    title: _unref(t)('participants.participants') + `\t(${participants.value.length}/${maxParticipants.value})`
  }, {
    default: _withCtx(() => [
      _createVNode(DataTable, {
        headers: participantsHeaders.value,
        "is-loading": participantsAreLoading.value,
        items: participantsItems.value,
        "search-value": participantsSearchValue.value,
        onEdit: handleEdit,
        onDelete: handleDelete
      }, null, 8, ["headers", "is-loading", "items", "search-value"]),
      _createVNode(NewActivityParticipant, {
        activityOccurrenceId: activityOccurrenceId.value,
        "onUpdate:activityOccurrenceId": _cache[0] || (_cache[0] = ($event: any) => ((activityOccurrenceId).value = $event)),
        isOnlineActivity: isOnline.value,
        "onUpdate:isOnlineActivity": _cache[1] || (_cache[1] = ($event: any) => ((isOnline).value = $event)),
        onNewParticipantAdded: _cache[2] || (_cache[2] = () => emit('newParticipantAdded'))
      }, null, 8, ["activityOccurrenceId", "isOnlineActivity"])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})